import React, { useMemo, useState } from 'react';
import { ArrowLeft, CalendarDays, FileText, Loader2, MapPin, MessageSquareText, Navigation } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AttendanceInput, AttendanceStatus, MeetingLocation, Participant } from '../../../context/LeadAttendanceContext';

interface RegisterAttendanceTabProps {
  participants: Participant[];
  isLoadingParticipants: boolean;
  isSubmitting: boolean;
  onBack?: () => void;
  onSubmitAttendance: (payload: {
    date: string;
    generalNotes: string;
    location?: MeetingLocation;
    attendances: AttendanceInput[];
  }) => Promise<void>;
}

const STATUS_OPTIONS: AttendanceStatus[] = ['PRESENT', 'ABSENT', 'LATE'];
const PARTICIPATION_SCORES = [1, 2, 3, 4, 5] as const;

const statusTheme: Record<AttendanceStatus, string> = {
  PRESENT: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  ABSENT: 'border-rose-300 bg-rose-50 text-rose-900',
  LATE: 'border-amber-300 bg-amber-50 text-amber-900',
};

const getTodayDateInput = () => new Date().toISOString().slice(0, 10);

export default function RegisterAttendanceTab({
  participants,
  isLoadingParticipants,
  isSubmitting,
  onBack,
  onSubmitAttendance,
}: RegisterAttendanceTabProps) {
  const [meetingDate, setMeetingDate] = useState(getTodayDateInput());
  const [generalNotes, setGeneralNotes] = useState('');
  const [locationMode, setLocationMode] = useState<'physical' | 'online'>('physical');
  const [meetingAddress, setMeetingAddress] = useState('');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [coordinates, setCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());
  const { t } = useTranslation();
  const [participantStates, setParticipantStates] = useState<Record<string, {
    status: AttendanceStatus;
    notes: string;
    participationScore: number;
  }>>({});

  const hydratedStates = useMemo(() => {
    const next: Record<string, {
      status: AttendanceStatus;
      notes: string;
      participationScore: number;
    }> = {};

    participants.forEach((participant) => {
      next[participant.id] = participantStates[participant.id] || {
        status: 'PRESENT',
        notes: '',
        participationScore: 3,
      };
    });

    return next;
  }, [participantStates, participants]);

  const onStatusChange = (participantId: string, status: AttendanceStatus) => {
    setParticipantStates((prev) => ({
      ...prev,
      [participantId]: {
        status,
        notes: prev[participantId]?.notes || '',
        participationScore: prev[participantId]?.participationScore || 3,
      },
    }));
  };

  const onMemberNotesChange = (participantId: string, notes: string) => {
    setParticipantStates((prev) => ({
      ...prev,
      [participantId]: {
        status: prev[participantId]?.status || 'PRESENT',
        notes,
        participationScore: prev[participantId]?.participationScore || 3,
      },
    }));
  };

  const onParticipationScoreChange = (participantId: string, participationScore: number) => {
    setParticipantStates((prev) => ({
      ...prev,
      [participantId]: {
        status: prev[participantId]?.status || 'PRESENT',
        notes: prev[participantId]?.notes || '',
        participationScore,
      },
    }));
  };

  const toggleMemberExpand = (participantId: string) => {
    setExpandedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(participantId)) {
        next.delete(participantId);
      } else {
        next.add(participantId);
      }
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const location: MeetingLocation | undefined =
      locationMode === 'online'
        ? {
            isVirtual: true,
            meetingUrl: meetingUrl.trim() || undefined,
          }
        : {
            isVirtual: false,
            address: meetingAddress.trim() || undefined,
            coordinates: coordinates || undefined,
          };

    const attendances: AttendanceInput[] = participants.map((participant) => ({
      participantId: participant.id,
      status: hydratedStates[participant.id]?.status || 'PRESENT',
      notes: hydratedStates[participant.id]?.notes?.trim() || '',
      participationScore:
        (hydratedStates[participant.id]?.status || 'PRESENT') === 'ABSENT'
          ? undefined
          : hydratedStates[participant.id]?.participationScore || 3,
    }));

    await onSubmitAttendance({
      date: new Date(`${meetingDate}T00:00:00Z`).toISOString(),
      generalNotes: generalNotes.trim(),
      location,
      attendances,
    });

    if (onBack) {
      onBack();
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {onBack && (
        <div className="flex justify-start">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-11 min-w-[48px] items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('backToCeremonies')}
          </button>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <label className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
            <CalendarDays className="h-4 w-4" />
            {t('meetingDate')}
          </span>
          <input
            type="date"
            value={meetingDate}
            onChange={(event) => setMeetingDate(event.target.value)}
            className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
            required
          />
        </label>

        <label className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
            <FileText className="h-4 w-4" />
            {t('generalAnnotation')}
          </span>
          <textarea
            value={generalNotes}
            onChange={(event) => setGeneralNotes(event.target.value)}
            className="h-28 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
            placeholder={t('generalNotesPlaceholder')}
          />
        </label>
      </div>

      <section className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-brand-700" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">{t('meetingLocation')}</h3>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setLocationMode('physical')}
            className={`inline-flex h-10 min-w-[110px] items-center justify-center rounded-xl border px-3 text-sm font-medium ${
              locationMode === 'physical'
                ? 'border-brand-300 bg-brand-50 text-brand-700'
                : 'border-slate-300 bg-white text-slate-700'
            }`}
          >
            {t('physical')}
          </button>
          <button
            type="button"
            onClick={() => setLocationMode('online')}
            className={`inline-flex h-10 min-w-[110px] items-center justify-center rounded-xl border px-3 text-sm font-medium ${
              locationMode === 'online'
                ? 'border-brand-300 bg-brand-50 text-brand-700'
                : 'border-slate-300 bg-white text-slate-700'
            }`}
          >
            {t('online')}
          </button>
        </div>

        {locationMode === 'physical' ? (
          <div className="space-y-2">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-slate-600">{t('addressOptional')}</span>
              <input
                type="text"
                value={meetingAddress}
                onChange={(event) => setMeetingAddress(event.target.value)}
                placeholder={t('addressPlaceholder')}
                className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
              />
            </label>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!navigator.geolocation) {
                    setLocationError(t('geolocationNotSupported'));
                    return;
                  }

                  setIsLocating(true);
                  setLocationError(null);

                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      setCoordinates({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                      });
                      setIsLocating(false);
                    },
                    (error) => {
                      setLocationError(error.message || t('couldNotGetLocation'));
                      setIsLocating(false);
                    },
                    { enableHighAccuracy: true, timeout: 12000 },
                  );
                }}
                disabled={isLocating}
                className="inline-flex h-10 min-w-[48px] items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                <Navigation className="h-4 w-4" />
                {isLocating ? t('gettingGps') : t('useMyGps')}
              </button>

              {coordinates && (
                <span className="text-xs text-slate-600">
                  {coordinates.latitude.toFixed(6)}, {coordinates.longitude.toFixed(6)}
                </span>
              )}
            </div>

            {locationError && <p className="text-xs text-rose-600">{locationError}</p>}
          </div>
        ) : (
          <label className="block space-y-1">
            <span className="text-xs font-medium text-slate-600">{t('meetingUrlOptional')}</span>
            <input
              type="url"
              value={meetingUrl}
              onChange={(event) => setMeetingUrl(event.target.value)}
              placeholder="https://meet.google.com/..."
              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
            />
          </label>
        )}
      </section>

      <section className="space-y-2 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{t('participantsLabel')}</h3>
          {isLoadingParticipants && <span className="text-xs text-slate-500">{t('loading')}</span>}
        </div>

        {isLoadingParticipants && (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="h-24 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        )}

        {!isLoadingParticipants && participants.length === 0 && (
          <p className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-sm text-slate-500">
            {t('noParticipantsFound')}
          </p>
        )}

        {participants.map((participant) => {
          const state = hydratedStates[participant.id];
          const isExpanded = expandedMembers.has(participant.id);

          return (
            <article key={participant.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="font-medium text-slate-900">{participant.name}</h4>
                  <p className="text-xs text-slate-500">{participant.phone || t('noPhone')}</p>
                </div>

                <button
                  type="button"
                  onClick={() => toggleMemberExpand(participant.id)}
                  className="inline-flex h-11 min-w-[48px] items-center justify-center rounded-xl border border-slate-300 bg-white px-3 text-xs font-medium text-slate-600 hover:bg-slate-100"
                >
                  <MessageSquareText className="mr-1 h-3.5 w-3.5" />
                  {isExpanded ? t('hide') : t('notes')}
                </button>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((status) => (
                  <label
                    key={status}
                    className={`inline-flex min-h-12 min-w-[110px] cursor-pointer items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${
                      state.status === status ? statusTheme[status] : 'border-slate-300 bg-white text-slate-700'
                    }`}
                  >
                    <input
                      type="radio"
                      className="h-4 w-4"
                      name={`status-${participant.id}`}
                      value={status}
                      checked={state.status === status}
                      onChange={() => onStatusChange(participant.id, status)}
                    />
                    {status}
                  </label>
                ))}
              </div>

              {state.status !== 'ABSENT' && (
                <div className="mt-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('participationScore')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {PARTICIPATION_SCORES.map((score) => {
                      const selected = state.participationScore === score;
                      return (
                        <button
                          key={`${participant.id}-score-${score}`}
                          type="button"
                          onClick={() => onParticipationScoreChange(participant.id, score)}
                          className={`inline-flex h-10 min-w-[44px] items-center justify-center rounded-lg border px-3 text-sm font-semibold transition ${
                            selected
                              ? 'border-brand-300 bg-brand-50 text-brand-700'
                              : 'border-slate-300 bg-white text-slate-700 hover:border-brand-200'
                          }`}
                          aria-label={`Set participation score ${score} for ${participant.name}`}
                          title={`Set participation score ${score}`}
                        >
                          {score}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {isExpanded && (
                <textarea
                  className="mt-3 h-20 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
                  placeholder={t('annotationPlaceholder')}
                  value={state.notes}
                  onChange={(event) => onMemberNotesChange(participant.id, event.target.value)}
                />
              )}
            </article>
          );
        })}
      </section>

      <button
        type="submit"
        disabled={isSubmitting || isLoadingParticipants || participants.length === 0}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-700 text-base font-semibold text-white transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('submitting')}
          </>
        ) : (
          t('submitAttendance')
        )}
      </button>
    </form>
  );
}
